<template>
    <StepShell :embedded="embedded">
        <template v-if="!hasProvider">
            <StepHeader
                :align="embedded ? 'start' : 'center'"
                :size="embedded ? 'step' : 'display'"
                title="Calendar"
                :lede="`${organizationName} hasn’t set up calendar access, so I’ll start from what you tell me. Everything else works the same.`" />

            <StepActions flush>
                <BaseButton theme="primary" :disabled="busy" @click="emit('skip')"> Continue </BaseButton>
            </StepActions>
        </template>

        <template v-else>
            <StepHeader :align="embedded ? 'start' : 'center'" :size="embedded ? 'step' : 'display'" title="Calendar">
                See where your week actually goes. I’ll read your last {{ SCAN_WINDOW_DAYS }} days of meetings: who you really work with, how much your standing meetings cost, and
                where the week goes.

                <template #note>
                    <details class="disclosure">
                        <summary class="disclosure-summary">
                            <i aria-hidden="true" class="bi bi-lock-fill"></i>
                            <span>Read-only, and nothing from your calendar is shared with anyone.</span>
                            <i aria-hidden="true" class="bi bi-chevron-down disclosure-chevron"></i>
                        </summary>

                        <!-- The separate panel prevents expanded disclosure content from blending into the
                             rest of the step.
                             -->
                        <div class="disclosure-panel">
                            <!-- Keep organization approval inside the disclosure to avoid duplicating
                                 reassurance in a separate box.
                                 -->
                            <p class="sanction">
                                <i aria-hidden="true" class="bi bi-patch-check-fill sanction-icon"></i>
                                <span>{{ organizationName }} has already approved calendar access for Nadia &mdash; the only permission left is yours.</span>
                            </p>

                            <p class="disclosure-heading">What I look at</p>

                            <ul class="disclosure-list">
                                <li v-for="item in READS" :key="item" class="disclosure-item">
                                    <i aria-hidden="true" class="bi bi-check-lg reads-icon"></i>
                                    <span class="reads-text">{{ item }}</span>
                                </li>
                                <li v-for="item in AVOIDS" :key="item" class="disclosure-item">
                                    <i aria-hidden="true" class="bi bi-x avoids-icon"></i>
                                    <span class="avoids-text">{{ item }}</span>
                                </li>
                            </ul>
                        </div>
                    </details>
                </template>
            </StepHeader>

            <!-- Show representative output before requesting access. Use two cards to communicate that
                 insights form a feed.
                 -->
            <figure class="sample">
                <figcaption class="sample-label">Example insights</figcaption>
                <InsightCardLayout v-for="reading in SAMPLE_READINGS" :key="reading.headline">
                    <h3 class="sample-headline">{{ reading.headline }}</h3>
                    <p class="sample-body">{{ reading.claim }} {{ reading.rest }}</p>
                </InsightCardLayout>
            </figure>

            <!--
                Confirm the timezone, as the calendar settings modal does: an incorrect inferred zone shifts
                meeting times throughout the insights.
            -->
            <div class="timezone">
                <p v-if="!editingTimezone" class="timezone-line">
                    Times shown in {{ timezoneName }}.
                    <BaseButton theme="tertiary" size="sm" class="timezone-change" @click="editingTimezone = true">Change</BaseButton>
                </p>
                <label v-else class="timezone-label" for="onboarding-timezone">
                    Your time zone
                    <BaseCombobox v-model="selectedTimezone" input-id="onboarding-timezone" :options="timezoneOptions" :disabled="busy" :clearable="false" />
                </label>
            </div>

            <StepActions flush>
                <!-- Linear onboarding needs skip to let users proceed without connecting. Guided setup
                     already provides navigation through its section rail.
                     -->
                <BaseButton v-if="!embedded" theme="tertiary" class="step-skip" :disabled="busy" @click="emit('skip')">Skip for now</BaseButton>
                <BaseButton theme="primary" :disabled="busy || selectedTimezone === null" @click="emit('connect', selectedTimezone!.value)">{{ connectLabel }}</BaseButton>
            </StepActions>
        </template>
    </StepShell>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import { computed, ref } from "vue";

import StepActions from "./StepActions.vue";
import StepHeader from "./StepHeader.vue";
import StepShell from "./StepShell.vue";
import type { CalendarContext } from "./types";

const { embedded = false, calendar, organizationName, busy } = defineProps<{ embedded?: boolean; calendar: CalendarContext; organizationName: string; busy: boolean }>();

const emit = defineEmits<{ connect: [timezone: string]; skip: [] }>();

interface TimezoneOption {
    label: string;
    value: string;
}

const timezoneOptions = computed<TimezoneOption[]>(() => calendar.timezones.map((zone) => ({ label: zone, value: zone })));

/* The browser's guess is a prefill, not a silent write: it fills the field so the
   user confirms one they can see, and nothing is saved until they press Connect. */
const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";

function initialTimezone(): TimezoneOption | null {
    const known = new Set(calendar.timezones);
    for (const candidate of [calendar.timezone, browserTimezone]) {
        if (candidate && known.has(candidate)) {
            return { label: candidate, value: candidate };
        }
    }
    return null;
}

const selectedTimezone = ref<TimezoneOption | null>(initialTimezone());

const editingTimezone = ref(selectedTimezone.value === null);

const timezoneName = computed(() => {
    const zone = selectedTimezone.value?.value;
    if (!zone) {
        return "your local time";
    }
    const city = zone.split("/").at(-1)?.replaceAll("_", " ") ?? zone;
    const label = new Intl.DateTimeFormat("en", { timeZone: zone, timeZoneName: "long" }).formatToParts(new Date()).find((part) => part.type === "timeZoneName")?.value;
    return label ? `${label} (${city})` : city;
});

const connectLabel = computed(() => {
    if (busy) {
        return `Opening ${calendar.provider_label}…`;
    }
    return selectedTimezone.value === null ? "Pick your time zone to continue" : `Connect ${calendar.provider_label}`;
});

/* Mirrors `coach_core.onboarding.calendar_first.SCAN_WINDOW_DAYS`. The payoff
   screen quotes the window the scan actually used, from its own stats. */
const SCAN_WINDOW_DAYS = 30;

/*
 * A privacy commitment, not marketing copy — so every line is what the scan
 * really requests. The prototype claimed event notes were read; they are not.
 * Titles are, but only inside the aggregation that groups a standing meeting's
 * occurrences: no title reaches a prompt, a reading, or another person. The
 * OAuth request sets `allow_private_events=false`, which is what makes the
 * private/personal exclusion below a real guarantee rather than a promise.
 */
/* Keep sample insights within the scan's actual capabilities. */
const SAMPLE_READINGS = [
    {
        headline: "Your Tuesdays are not yours",
        claim: "11 of your 32 weekly meeting hours land on one day.",
        rest: "The other four average five.",
    },
    {
        headline: "Your standing meetings run the week",
        claim: "Six recurring meetings take 9 hours a week between them.",
        rest: "None of them is shorter than 45 minutes.",
    },
];

const READS = [
    "Who was in each meeting, and how often you meet with them",
    "How long each meeting ran, and which ones repeat",
    "Meeting titles, only to tell your standing meetings apart from each other",
];

const AVOIDS = ["Never reads meeting notes, descriptions, or attachments", "Never reads anything marked private or personal"];

const hasProvider = computed(() => calendar.provider.length > 0);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .disclosure-panel {
        @apply mt-2.5 flex flex-col gap-3 rounded-lgr bg-surface-sunken p-4;
    }

    .sanction {
        @apply body-small flex items-start gap-2.5 text-ink;
    }

    .disclosure-summary > .bi-lock-fill {
        @apply shrink-0;
    }

    .sanction-icon {
        @apply mt-0.5 shrink-0 text-ink-muted;
    }

    .timezone {
        @apply -mt-4 flex w-full flex-col gap-2;
    }

    .timezone-line {
        @apply details flex items-center gap-1 text-ink-muted;
    }

    .timezone-change {
        @apply details -ml-1 text-[12px] leading-normal font-normal text-chip-nadia-text;
    }

    .timezone-label {
        @apply body-small-semibold flex flex-col gap-2 text-ink;
    }

    .sample {
        @apply flex w-full flex-col gap-1.5;
    }

    .sample-label {
        @apply scaffold-details-small text-ink-muted;
    }

    .sample-headline {
        @apply nadia-subtitle text-ink;
    }

    .sample-body {
        @apply body-small mt-2 text-ink-muted;
    }

    .disclosure {
        @apply mt-6 w-full;
    }

    .disclosure-summary {
        @apply details inline-flex w-auto cursor-pointer list-none items-center gap-1 text-ink-muted hover:text-ink;
    }

    .disclosure-summary::-webkit-details-marker {
        display: none;
    }

    .disclosure-chevron {
        @apply shrink-0 text-chip-nadia-text transition-transform;
    }

    .disclosure[open] .disclosure-chevron {
        @apply rotate-180;
    }

    /* Extra space above the heading groups it with the list rather than the preceding paragraph. */
    .disclosure-heading {
        @apply details mt-1 tracking-wide text-ink-muted uppercase;
    }

    .disclosure-list {
        @apply -mt-1 flex flex-col gap-2;
    }

    .disclosure-item {
        @apply flex items-start gap-2.5;
    }

    .reads-icon {
        @apply shrink-0 text-ink-muted;
    }

    .reads-text {
        @apply body-small text-ink;
    }

    .avoids-icon {
        @apply shrink-0 text-ink-muted;
    }

    .avoids-text {
        @apply body-small text-ink-muted;
    }
}

@media (prefers-reduced-motion: reduce) {
    .disclosure-chevron {
        transition: none;
    }
}
</style>
